import React, { FC } from 'react'
import { useGetBillingApprovalSchedule } from './hooks'
import { useAppRouter, useCalculatedPaymentSchedules, useCohort, useIsBillingAgreementApprovalProcess, useIsCheckoutLoadingOrUpdating } from '../../state'
import { CheckoutPaymentSchedule, Cohort } from '@afterpaytouch/portal-api'
import { TextSizes, Timeline, useDate } from '@afterpaytouch/core'
import styles from './style.module.css'
import { useTranslation } from 'react-i18next'
import { Money } from '../Money'

interface VerticalPaymentScheduleTimelineProps {
  testNameSpace?: string
}

const BillingApprovalTimeline: FC<{ testNameSpace?: string }> = ({ testNameSpace }) => {
  const { t } = useTranslation()
  const cohort = useCohort()
  const isAPOrNetNewCohort = cohort === Cohort.APOnly || cohort === Cohort.NetNew
  const billingApprovalSchedule = useGetBillingApprovalSchedule()

  if (billingApprovalSchedule === null) {
    return null
  }

  return (
    <Timeline kind='Vertical' testNameSpace={testNameSpace}>
      {billingApprovalSchedule.installmentTimeline.map((numWeeks: number, idx: number) => {
        const isLast = idx === billingApprovalSchedule.installmentTimeline.length - 1
        const isFirst = idx === 0
        const titleText =
          numWeeks === 0
            ? t('summary:billingApprovalSchedule.firstTimelineItemLabel')
            : t('summary:billingApprovalSchedule.timelineItemLabel', { numWeeks, context: isAPOrNetNewCohort ? 'estimated' : '' })

        const bodyText = {
          text: t('summary:billingApprovalSchedule.timelineItemValue', { percentage: billingApprovalSchedule?.installmentPercentage }),
          styling: {
            size: 'XS' as TextSizes,
            bold: true,
          },
        }

        return (
          <Timeline.Item
            key={idx}
            hideLine={isLast}
            highlight={isFirst}
            kind='Vertical'
            titleText={titleText}
            bodyText={bodyText}
            testNameSpace={testNameSpace}
          />
        )
      })}
    </Timeline>
  )
}

const Pi4Timeline: FC<{ testNameSpace?: string }> = ({ testNameSpace }) => {
  const { locale } = useAppRouter()
  const { formatDate } = useDate({ locale, weekday: 'short' })
  const { t } = useTranslation()
  const schedules = useCalculatedPaymentSchedules()
  const isLoadingOrUpdating = useIsCheckoutLoadingOrUpdating()

  return (
    <Timeline kind='Vertical' testNameSpace={testNameSpace}>
      {schedules.map((schedule: CheckoutPaymentSchedule, idx: number) => {
        const isFirst = idx === 0
        const isLast = idx === schedules.length - 1
        const date = formatDate(schedule.installmentDueDate)
        const dateText = isFirst ? `${t('today')} - ${date}` : date

        return (
          <Timeline.Item
            key={idx}
            hideLine={isLast}
            highlight={isFirst}
            kind='Vertical'
            titleText={<span className={isLast ? styles.lastTimelineContainer : styles.timelineItemContainer}>{dateText}</span>}
            bodyText={{
              text: (
                <span data-testid={`paymentScheduleItem-money-${idx}`}>
                  <Money loading={isLoadingOrUpdating} bold={true} value={schedule.amount} />
                </span>
              ),
              styling: {
                size: 'XS' as TextSizes,
                bold: true,
              },
            }}
            testNameSpace={testNameSpace}
          />
        )
      })}
    </Timeline>
  )
}

export const VerticalPaymentScheduleTimeline: FC<VerticalPaymentScheduleTimelineProps> = ({ testNameSpace }: VerticalPaymentScheduleTimelineProps) => {
  const isBillingAgreementApprovalProcess = useIsBillingAgreementApprovalProcess()

  return isBillingAgreementApprovalProcess ? <BillingApprovalTimeline testNameSpace={testNameSpace} /> : <Pi4Timeline testNameSpace={testNameSpace} />
}
